import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errText } from "@/lib/api";
import { BackupCodes, CodeInput } from "@/components/account/BackupCodes";

const COPY = {
  disable: ["Turn off two-step sign-in?", "Enter a current code (or a backup code) to confirm. You'll sign in with just your password or Google after this.", "/mfa/disable", "Turn it off"],
  backup: ["Get new backup codes", "Enter a current code. Your old backup codes stop working straight away.", "/mfa/backup-codes", "Make new codes"],
};

export const MfaCodeDialog = ({ action, method, onClose }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [title, desc, path, cta] = COPY[action];
  const backupOk = action === "disable";

  const email = async () => {
    try { toast.success(`Code sent to ${(await api.post("/mfa/email-code")).sent_to}`); }
    catch (e) { toast.error(errText(e, "Couldn't send a code")); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(path, { code });
      qc.invalidateQueries({ queryKey: ["mfa"] }); qc.invalidateQueries({ queryKey: ["me"] });
      if (r.backup_codes) setCodes(r.backup_codes);
      else { toast.success("Two-step sign-in is off"); onClose(); }
    } catch (e) { toast.error(errText(e, "That code didn't work")); setCode(""); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`mfa-${action}-dialog`} className="max-w-md bg-[#121212] border-[#2A2A2A] text-white">
        <DialogHeader>
          <DialogTitle>{codes ? "Your new backup codes" : title}</DialogTitle>
          {!codes && <DialogDescription className="text-[#A3A3A3]">{desc}</DialogDescription>}
        </DialogHeader>
        {codes ? (
          <><BackupCodes codes={codes} /><button data-testid="mfa-backup-done" onClick={onClose} className="rounded-full bg-[#00FF9D] py-2.5 text-sm font-semibold text-[#050505]">Done</button></>
        ) : (
          <>
            <CodeInput testid={`mfa-${action}-code`} value={code} onChange={setCode} backup={backupOk} />
            {method === "email" && <button data-testid={`mfa-${action}-email-code`} onClick={email} className="text-left text-xs text-[#A3A3A3] underline hover:text-white">Email me a code</button>}
            <button data-testid={`mfa-${action}-confirm`} onClick={submit} disabled={busy || code.replace(/\W/g, "").length < 6}
              className={`rounded-full py-2.5 text-sm font-semibold text-[#050505] disabled:opacity-40 ${action === "disable" ? "bg-[#FF5C7A]" : "bg-[#00FF9D]"}`}>{busy ? "Checking…" : cta}</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
